import type { DaySchedule, TimeRange } from './db/types';

export type BusinessDayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface BusinessBlockedPeriod {
  id: string;
  label: string;
  startTime: Date | string | any;
  endTime: Date | string | any;
}

export interface BusinessLogicSettings {
  workingPlan: Record<BusinessDayKey, DaySchedule | null>;
  defaultBreaks: Array<{
    id: string;
    day: BusinessDayKey;
    start: string;
    end: string;
  }>;
  blockedPeriods: BusinessBlockedPeriod[];
  bookingTimeoutMinutes: number;
}

export const businessDays: Array<{ key: BusinessDayKey; label: string }> = [
  { key: 'sunday', label: 'Sunday' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
];

export const defaultBusinessWorkingPlan: BusinessLogicSettings['workingPlan'] = {
  sunday: null,
  monday: { isWorking: true, start: '09:00', end: '17:00', breaks: [] },
  tuesday: { isWorking: true, start: '09:00', end: '17:00', breaks: [] },
  wednesday: { isWorking: true, start: '09:00', end: '17:00', breaks: [] },
  thursday: { isWorking: true, start: '09:00', end: '17:00', breaks: [] },
  friday: { isWorking: true, start: '09:00', end: '17:00', breaks: [] },
  saturday: null,
};

export const defaultBusinessLogicSettings: BusinessLogicSettings = {
  workingPlan: defaultBusinessWorkingPlan,
  defaultBreaks: [],
  blockedPeriods: [],
  bookingTimeoutMinutes: 60,
};

export function applyBreaksToWorkingPlan(
  workingPlan: BusinessLogicSettings['workingPlan'],
  breaks: BusinessLogicSettings['defaultBreaks']
): BusinessLogicSettings['workingPlan'] {
  const next = { ...workingPlan };
  businessDays.forEach(({ key }) => {
    const day = next[key];
    if (!day?.isWorking) return;
    const dayBreaks: TimeRange[] = breaks
      .filter((breakPeriod) => breakPeriod.day === key)
      .map((breakPeriod) => ({ start: breakPeriod.start, end: breakPeriod.end }));
    next[key] = {
      ...day,
      breaks: dayBreaks,
    };
  });
  return next;
}

export function mergeBusinessLogicSettings(value: any): BusinessLogicSettings {
  const source = value || {};
  const workingPlan = { ...defaultBusinessWorkingPlan, ...(source.workingPlan || {}) };
  const defaultBreaks = Array.isArray(source.defaultBreaks) ? source.defaultBreaks : [];
  return {
    workingPlan: applyBreaksToWorkingPlan(workingPlan, defaultBreaks),
    defaultBreaks,
    blockedPeriods: Array.isArray(source.blockedPeriods) ? source.blockedPeriods : [],
    bookingTimeoutMinutes:
      typeof source.bookingTimeoutMinutes === 'number'
        ? Math.max(0, Math.round(source.bookingTimeoutMinutes))
        : defaultBusinessLogicSettings.bookingTimeoutMinutes,
  };
}
